// ABOUTME: Displays a streaming log of agent reasoning/rationales during execution.
// ABOUTME: Shows thought process transparency as tools are called in real-time.

import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Zap } from 'lucide-react';

export interface RationaleEntry {
  id: string;
  toolName: string;
  rationale: string;
  timestamp: number;
}

interface RationaleLogProps {
  entries: RationaleEntry[];
  currentPhase?: string;
  isActive?: boolean;
  isLoading?: boolean;
}

export function RationaleLog({ entries, currentPhase, isActive = true, isLoading = false }: RationaleLogProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new entries are added
  useEffect(() => {
    if (scrollContainerRef.current && entries.length > 0) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [entries.length]);

  if (entries.length === 0 && !currentPhase && !isActive && !isLoading) {
    return null;
  }

  // Static view for card back (technical details)
  if (!isActive) {
    return (
      <div className="rounded-sm bg-gradient-to-br from-primary-50 to-surface-100 dark:from-primary-100 dark:to-surface-100 border border-primary-200 dark:border-primary-300 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-primary-200 dark:border-primary-300 bg-primary-50 dark:bg-primary-100">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-600">
              Thought Process
            </span>
          </div>
        </div>

        {/* Rationale entries */}
        <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
          {entries.map((entry, index) => (
            <div key={entry.id} className="flex gap-3">
              {/* Step number */}
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-200 flex items-center justify-center">
                <span className="text-xs font-medium text-primary-600">
                  {index + 1}
                </span>
              </div>

              {/* Rationale content */}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-surface-500 mb-1">
                  {getToolLabel(entry.toolName)}
                </div>
                <p className="text-sm text-surface-600 leading-relaxed">
                  {entry.rationale}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasEntries = entries.length > 0;

  // No entries yet - show loading card with spinner
  if (!hasEntries) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="card rounded-sm overflow-hidden"
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-4 h-4 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            <span className="text-sm font-medium text-surface-800">
              Thinking
            </span>
            <div className="flex gap-1 ml-1">
              <motion.span
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
                className="w-1 h-1 rounded-full bg-primary-500"
              />
              <motion.span
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
                className="w-1 h-1 rounded-full bg-primary-500"
              />
              <motion.span
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
                className="w-1 h-1 rounded-full bg-primary-500"
              />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Active streaming view with entries - show the card
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="card rounded-sm overflow-hidden"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <motion.div
            className="animate-pulse"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Zap className="w-4 h-4 text-primary-500" />
          </motion.div>
          <span className="text-sm font-medium text-surface-800">
            Working on it
          </span>
          <div className="flex gap-1 ml-1">
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
              className="w-1 h-1 rounded-full bg-primary-500"
            />
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
              className="w-1 h-1 rounded-full bg-primary-500"
            />
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
              className="w-1 h-1 rounded-full bg-primary-500"
            />
          </div>
        </div>

        {/* Rationale entries */}
        <div ref={scrollContainerRef} className="space-y-2 max-h-[400px] overflow-y-auto">
          <AnimatePresence mode="popLayout">
            {entries.map((entry, index) => {
              const isLatest = index === entries.length - 1;
              // Check if this is the generate_insight tool (exact match or MCP prefixed like mcp__tools__generate_insight)
              const isInsightTool = entry.toolName === 'generate_insight' ||
                entry.toolName === 'mcp__tools__generate_insight';

              // Special rendering for generate_insight tool - accent style with shimmer text
              if (isInsightTool && isLatest) {
                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-start gap-3"
                  >
                    {/* Accent glowing dot */}
                    <div className="flex flex-col items-center pt-1.5">
                      <div className="w-2 h-2 rounded-full bg-accent-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]" />
                    </div>

                    {/* Content with shimmer text */}
                    <div className="flex-1 min-w-0 pb-3">
                      <motion.p
                        className="text-sm leading-relaxed"
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <span className="font-medium text-accent-600">
                          Creating insight:
                        </span>{' '}
                        <span className="text-surface-800">{entry.rationale}</span>
                      </motion.p>
                    </div>
                  </motion.div>
                );
              }

              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-start gap-3"
                >
                  {/* Vertical line + dot indicator */}
                  <div className="flex flex-col items-center pt-1.5">
                    <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      isLatest
                        ? 'bg-primary-500 shadow-[0_0_8px_rgba(20,184,166,0.6)]'
                        : 'bg-surface-300'
                    }`} />
                    {index !== entries.length - 1 && (
                      <div className="w-px h-full min-h-[20px] bg-surface-200 mt-1" />
                    )}
                  </div>

                  {/* Rationale content */}
                  <div className="flex-1 min-w-0 pb-3">
                    <motion.p
                      className={`text-sm leading-relaxed transition-colors duration-300 ${
                        isLatest ? 'text-surface-800' : 'text-surface-500'
                      }`}
                      animate={isLatest ? { opacity: [0.7, 1, 0.7] } : { opacity: 1 }}
                      transition={isLatest ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : {}}
                    >
                      <span className={`font-medium ${isLatest ? 'text-primary-600' : 'text-surface-400'}`}>
                        {getToolLabel(entry.toolName)}:
                      </span>{' '}
                      {entry.rationale}
                    </motion.p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

// Helper to normalize MCP tool names (remove mcp__*__ prefix)
function normalizeToolName(name: string): string {
  // Handle MCP prefixed names like mcp__obsidian__execute_sql or mcp__tools__execute_sql
  const mcpMatch = name.match(/^mcp__[^_]+__(.+)$/);
  return mcpMatch ? mcpMatch[1] : name;
}

function getToolLabel(toolName: string): string {
  const normalizedName = normalizeToolName(toolName);

  const labels: Record<string, string> = {
    execute_sql: 'SQL Query',
    sql_query: 'SQL Query',
    execute_python: 'Python Analysis',
    python_execution: 'Python Analysis',
    generate_python_visualization: 'Python Visualization',
    python_visualization: 'Python Visualization',
    discover_schema: 'Schema Discovery',
    analyze_schema: 'Schema Discovery',
    schema_discovery: 'Schema Discovery',
    estimate_cost: 'Cost Estimation',
    generate_insight: 'Creating Insight',
    generate_chart: 'Generate Chart',
  };

  return labels[normalizedName] || normalizedName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}
